import React, { useState, useEffect } from "react";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    FormControl,
    FormLabel,
    Input,
    Button,
    VStack,
    Text,
    Select,
    Switch,
    Box,
    HStack,
    IconButton,
    Wrap,
    WrapItem,
    Tag,
    Spinner,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import { useDispatch, useSelector } from "react-redux";
import {
    create_coupon,
    update_coupon,
} from "../../redux/couponSlice";
import SearchableDropdown from "../../components/SearchableDropdown";

const CreateUpdateCoupon = ({
    isOpen,
    onClose,
    isEditMode,
    initialFormData,
    initialSelectedProducts,
    initialSelectedCustomerIds,
    availableProducts,
    usedProductCodes,
    product_list,
    onProductSelect,
    onProductRemove,
    selectedProducts,
    setSelectedProducts,
    selectedCustomerIds,
    setSelectedCustomerIds,
}) => {
    const dispatch = useDispatch();
    const { loading } = useSelector((state) => state.coupon);
    const { all_customer_list } = useSelector((state) => state.customers);

    const [formData, setFormData] = useState({
        id: "",
        coupon_code: "",
        description: "",
        discount_amount: "",
        additional_discount: "",
        validity: "",
        status: true,
    });

    // Update form data when initialFormData changes
    useEffect(() => {
        if (initialFormData) {
            setFormData(initialFormData);
        }
    }, [initialFormData]);

    // Handle input change
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // Handle status toggle
    const handleStatusToggle = (checked) => {
        setFormData((prev) => ({ ...prev, status: checked }));
    };

    // Handle customer selection
    const handleCustomerSelect = (e) => {
        const value = e.target.value;
        if (value && !selectedCustomerIds.includes(value)) {
            setSelectedCustomerIds((prev) => [...prev, value]);
        }
    };

    // Handle customer removal
    const handleRemoveCustomer = (id) => {
        setSelectedCustomerIds((prev) => prev.filter((cid) => cid !== id));
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
    
        const formattedData = {
            ...formData,
            validity: formData.validity.replace("T", " ") + ":00",
            status: formData.status ? "1" : "0",
            prod_code: selectedProducts.map((p) => p.product_code), // This should now have the correct data
        };
    
        // Handle customer_id logic
        if (isEditMode) {
            if (selectedCustomerIds.length > 0) {
                formattedData.customer_id = selectedCustomerIds;
            }
            dispatch(update_coupon(formattedData));
        } else {
            if (selectedCustomerIds.length > 0) {
                formattedData.customer_id = selectedCustomerIds;
            }
            dispatch(create_coupon(formattedData));
        }
        
        console.log("Selected Products before submit:", selectedProducts);
        console.log("Formatted Data:", formattedData);
    };

// Set form data when modal opens
useEffect(() => {
    if (!isOpen) return;
    if (initialFormData) setFormData(initialFormData);
}, [isOpen]);

// Set selected products when modal opens
useEffect(() => {
    if (!isOpen) return;
    if (initialSelectedProducts) setSelectedProducts(initialSelectedProducts);
}, [isOpen]);


    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
            <ModalOverlay />
            <ModalContent maxH="90vh" overflowY="auto">
                <ModalHeader>
                    {isEditMode ? "Edit Coupon" : "Create New Coupon"}
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <VStack spacing={4} align="stretch">
                        <FormControl isRequired>
                            <FormLabel>Coupon Code</FormLabel>
                            <Input
                                name="coupon_code"
                                value={formData.coupon_code}
                                onChange={handleChange}
                                placeholder="Enter coupon code"
                            />
                        </FormControl>

                        <FormControl isRequired>
                            <FormLabel>Description</FormLabel>
                            <Input
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                placeholder="Enter description"
                            />
                        </FormControl>

                        <FormControl>
    <FormLabel>Select Customer (Optional)</FormLabel>

    <SearchableDropdown
        options={all_customer_list || []}
        value={null} // always null so user can select unlimited customers
        onChange={(customer) => {
            if (customer && customer.id && !selectedCustomerIds.includes(customer.id)) {
                setSelectedCustomerIds((prev) => [...prev, customer.id]);
            }
        }}
        placeholder="Select customer"
        searchPlaceholder="Search by name, business, email, phone..."
        getOptionLabel={(customer) =>
            customer.name ||
            customer.business_name ||
            `Customer ${customer.id}`
        }
        getOptionValue={(customer) => customer.id}
        getOptionSubLabel={(customer) =>
            customer.mobile ||
            customer.email ||
            ""
        }
        size="md"
        variant="outline"
        matchWidth={true}
        maxHeight="300px"
    />

    {/* Selected customer tags */}
    {selectedCustomerIds.length > 0 && (
        <Wrap mt={2}>
            {selectedCustomerIds.map((id) => {
                const cust = all_customer_list.find((c) => c.id === id);
                return (
                    <WrapItem key={id}>
                        <Tag colorScheme="blue" borderRadius="full">
                            <Text fontSize="sm" mr={1}>
                                {cust?.name ||
                                    cust?.business_name ||
                                    `Customer ${cust?.id}`}
                            </Text>
                            <IconButton
                                icon={<CloseIcon />}
                                size="xs"
                                variant="ghost"
                                onClick={() => handleRemoveCustomer(id)}
                                aria-label="Remove customer"
                            />
                        </Tag>
                    </WrapItem>
                );
            })}
        </Wrap>
    )}
</FormControl>


                        <FormControl>
                            <FormLabel>
                                Select Products {selectedProducts.length === 0 && <Text as="span" color="red.500">*</Text>}
                                {isEditMode && <Text as="span" fontSize="xs" color="gray.500" ml={2}>(Product selection disabled in edit mode)</Text>}
                            </FormLabel>
                            <Select
                                placeholder={isEditMode ? "Product selection is disabled in edit mode" : "Select a product"}
                                onChange={(e) => {
                                    if (isEditMode) return;
                                    onProductSelect(e);
                                }}
                                value=""
                                isDisabled={isEditMode}
                            >
                                {availableProducts.map((product) => (
                                    <option key={product.product_code} value={product.product_code}>
                                        {product.type}-{product.name} ({product.product_code}) - RSP: ₹{product.rsp}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>

                        {selectedProducts.length > 0 && (
                            <Box
                                borderWidth="1px"
                                borderRadius="md"
                                p={3}
                                bg="gray.50"
                                maxH="200px"
                                overflowY="auto"
                            >
                                <Text fontWeight="semibold" mb={2} fontSize="sm">
                                    Selected Products ({selectedProducts.length})
                                </Text>
                                <VStack spacing={2} align="stretch">
                                    {selectedProducts.map((product) => (
                                        <HStack
                                            key={product.product_code}
                                            justify="space-between"
                                            p={2}
                                            bg="white"
                                            borderRadius="md"
                                            borderWidth="1px"
                                        >
                                            <Box flex="1">
                                                <Text fontSize="sm" fontWeight="medium">
                                                    {product.name}
                                                </Text>
                                                <Text fontSize="xs" color="gray.600">
                                                    Code: {product.product_code}
                                                </Text>
                                            </Box>
                                            <Box textAlign="right">
                                                <Text fontSize="sm" fontWeight="semibold" color="green.600">
                                                    RSP: ₹{product.rsp}
                                                </Text>
                                            </Box>
                                            <IconButton
                                                size="sm"
                                                icon={<CloseIcon />}
                                                colorScheme="red"
                                                variant="ghost"
                                                onClick={() => onProductRemove(product.product_code)}
                                                aria-label="Remove product"
                                                isDisabled={isEditMode}
                                            />
                                        </HStack>
                                    ))}
                                </VStack>
                            </Box>
                        )}

                        <FormControl isRequired>
                            <FormLabel>Discount Amount (₹)</FormLabel>
                            <Input
                                type="number"
                                name="discount_amount"
                                value={formData.discount_amount}
                                onChange={handleChange}
                                placeholder="Enter discount amount in rupees"
                                min="0"
                                step="0.01"
                            />
                            <Text fontSize="xs" color="gray.500" mt={1}>
                                Enter the discount amount in rupees (can be any amount)
                            </Text>
                        </FormControl>

                        <FormControl isRequired>
                            <FormLabel>Online Prepaid Discount (₹)</FormLabel>
                            <Input
                                type="number"
                                name="additional_discount"
                                value={formData.additional_discount}
                                onChange={handleChange}
                                placeholder="Enter discount amount in rupees"
                                min="0"
                                step="0.01"
                            />
                            <Text fontSize="xs" color="gray.500" mt={1}>
                                Enter the Online Prepaid amount in rupees (can be any amount)
                            </Text>
                        </FormControl>

                        <FormControl isRequired>
                            <FormLabel>Validity</FormLabel>
                            <Input
                                type="datetime-local"
                                name="validity"
                                value={formData.validity}
                                onChange={handleChange}
                            />
                        </FormControl>

                        <FormControl display="flex" alignItems="center">
                            <FormLabel htmlFor="status-toggle" mb="0">
                                Status
                            </FormLabel>
                            <Switch
                                id="status-toggle"
                                isChecked={formData.status}
                                onChange={(e) => handleStatusToggle(e.target.checked)}
                                colorScheme="green"
                                size="lg"
                            />
                            <Text ml={3} fontSize="sm" fontWeight="medium" color={formData.status ? "green.600" : "red.600"}>
                                {formData.status ? "Active" : "Inactive"}
                            </Text>
                        </FormControl>
                    </VStack>
                </ModalBody>

                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        colorScheme="green"
                        onClick={handleSubmit}
                        isDisabled={loading || (!isEditMode && selectedProducts.length === 0)}
                        leftIcon={loading && <Spinner size="sm" />}
                    >
                        {loading
                            ? isEditMode
                                ? "Updating..."
                                : "Creating..."
                            : isEditMode
                                ? "Update"
                                : "Create"}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default CreateUpdateCoupon;